import React, { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { LANGS, COMPANY } from "@/lib/translations";

const Navbar = () => {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinks = [
    { href: "#services", label: t.nav.services, testid: "nav-services" },
    { href: "#tours", label: t.nav.tours, testid: "nav-tours" },
    { href: "#booking", label: t.nav.booking, testid: "nav-booking" },
    { href: "#contact", label: t.nav.contact, testid: "nav-contact" },
  ];

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        scrolled ? "glass-nav" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <a href="#top" data-testid="logo-home" className="flex items-center gap-3 group">
          <img
            src={COMPANY.logo}
            alt="Faustitur Tourism Taxi"
            className="h-11 w-11 object-cover rounded-sm border border-white/10"
          />
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-display text-white text-lg tracking-wide">Faustitur</span>
            <span className="text-[10px] tracking-[0.3em] uppercase text-[color:var(--teal)]">
              Tourism Taxi
            </span>
          </div>
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {navLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.testid}
              className="text-sm text-zinc-300 hover:text-[color:var(--gold)] transition-colors tracking-wide"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center border border-white/10 rounded-sm overflow-hidden">
            {LANGS.map((l) => (
              <button
                key={l.code}
                data-testid={`lang-${l.code}`}
                onClick={() => setLang(l.code)}
                className={`px-3 py-1.5 text-xs font-medium tracking-wider transition-colors ${
                  lang === l.code
                    ? "bg-[color:var(--gold)] text-black"
                    : "text-zinc-300 hover:text-[color:var(--gold)]"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>

          <a
            href={COMPANY.telLink}
            data-testid="navbar-call-btn"
            className="hidden md:inline-flex btn-gold !py-2.5 !px-4 text-sm"
          >
            <Phone className="h-4 w-4" />
            <span className="hidden lg:inline">{COMPANY.phoneDisplay}</span>
          </a>

          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-white p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden border-t border-white/10 bg-black/95 backdrop-blur-xl"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {navLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`mobile-${l.testid}`}
                onClick={() => setOpen(false)}
                className="text-zinc-200 text-base hover:text-[color:var(--gold)] transition-colors"
              >
                {l.label}
              </a>
            ))}
            <div className="flex items-center gap-2 pt-2">
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  data-testid={`mobile-lang-${l.code}`}
                  onClick={() => setLang(l.code)}
                  className={`px-3 py-1.5 text-xs font-medium tracking-wider border transition-colors ${
                    lang === l.code
                      ? "bg-[color:var(--gold)] text-black border-[color:var(--gold)]"
                      : "text-zinc-300 border-white/10 hover:text-[color:var(--gold)]"
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
            <a
              href={COMPANY.telLink}
              data-testid="mobile-call-btn"
              className="btn-gold justify-center mt-2"
            >
              <Phone className="h-4 w-4" /> {COMPANY.phoneDisplay}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
